import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PrintApiModel } from '@app/api/api-models/reports/PrintApiModel';
import {
  ComparisonSelectorsApiModel,
  PendingTasksReportRequestApiModel,
  CustomQuestionsReportFiltersRequestApiModel,
  CustomQuestionsReportFiltersApiModel,
  CustomQuestionsReportRequestApiModel,
  CustomQuestionsReportApiModel,
  TrendsReportFiltersApiModel,
  TrendsReportRequestApiModel,
  TrendsReportApiModel,
  TraineePerformanceReportFiltersApiModel,
  TraineePerformanceReportRequestApiModel,
  TraineePerformanceReportApiModel,
  TrainerPerformanceReportFiltersApiModel,
  TrainerPerformanceReportRequestApiModel,
  TrainerPerformanceReportApiModel,
  CallTypesReportRequestApiModel,
  CallTypesReportApiModel,
  CallTypesReportFiltersApiModel
} from '@app/api/api-models';
import { PendingTasksReportApiModel } from '@app/api/api-models/reports/pending-tasks-report/PendingTasksReportApiModel';
import { PendingTasksReportFiltersApiModel } from '@app/api/api-models/reports/pending-tasks-report/PendingTasksReportFiltersApiModel';
import {
  SkillsReportFiltersApiModel,
  SkillsReportRequestApiModel,
  SkillsReportApiModel
} from '@app/api/api-models/reports/skills-reports';
import { ProgramRole } from '@app/shared/enums';
import {
  RemedialActivitiesFiltersApiModel,
  RemedialActivitiesReportApiModel,
  RemedialActivitiesReportRequestApiModel
} from '@app/api/api-models/reports/remedial-activities-reports';
import {
  TrainerHoursReportFiltersApiModel,
  TrainerHoursReportRequestApiModel,
  TrainerHoursReportApiModel
} from '@app/api/api-models/reports/trainer-hours';
import { HttpClientService } from '@app/api/api-services/http-client.service';

@Injectable()
export class ReportsService {
  public apiUrl = 'reports';

  constructor(private http: HttpClientService) {}

  public getComparisonSelectors(role?: ProgramRole): Observable<ComparisonSelectorsApiModel> {
    return this.http.get<ComparisonSelectorsApiModel>(`${this.apiUrl}/comparison-selectors?role=${role}`);
  }

  public getPendingTasksFilterData(): Observable<PendingTasksReportFiltersApiModel> {
    return this.http
      .get<PendingTasksReportFiltersApiModel>(`${this.apiUrl}/pending-tasks/filters`)
      .pipe(map((data) => new PendingTasksReportFiltersApiModel().deserialize(data)));
  }

  public getPendingTasksReport(filters: PendingTasksReportRequestApiModel): Observable<PendingTasksReportApiModel> {
    return this.http.post<PendingTasksReportApiModel>(`${this.apiUrl}/pending-tasks/report`, filters);
  }

  public getPendingTasksPrint(model: PrintApiModel<PendingTasksReportRequestApiModel>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/pending-tasks/pdf`, model, { responseType: 'blob' });
  }

  public getPendingTasksXlsx(filters: PendingTasksReportRequestApiModel): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/pending-tasks/xlsx`, filters, { responseType: 'blob' });
  }

  public getRemedialActivitiesFilterData(): Observable<RemedialActivitiesFiltersApiModel> {
    return this.http
      .get<RemedialActivitiesFiltersApiModel>(`${this.apiUrl}/remedial-activities/filters`)
      .pipe(map((data) => new RemedialActivitiesFiltersApiModel().deserialize(data)));
  }

  public getRemedialActivitiesReport(
    filters: RemedialActivitiesReportRequestApiModel
  ): Observable<RemedialActivitiesReportApiModel> {
    return this.http.post<RemedialActivitiesReportApiModel>(`${this.apiUrl}/remedial-activities/report`, filters);
  }

  public getRemedialActivitiesPrint(model: PrintApiModel<RemedialActivitiesReportRequestApiModel>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/remedial-activities/pdf`, model, { responseType: 'blob' });
  }

  public getRemedialActivitiesXlsx(filters: RemedialActivitiesReportRequestApiModel): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/remedial-activities/xlsx`, filters, { responseType: 'blob' });
  }

  public getSkillsFilterData(): Observable<SkillsReportFiltersApiModel> {
    return this.http.get<SkillsReportFiltersApiModel>(`${this.apiUrl}/skills/filters`);
  }

  public getCallTypesFilterData(): Observable<CallTypesReportFiltersApiModel> {
    return this.http.get<CallTypesReportFiltersApiModel>(`${this.apiUrl}/call-types/filters`);
  }

  public getSkillsReport(model: SkillsReportRequestApiModel): Observable<SkillsReportApiModel> {
    return this.http.post<SkillsReportApiModel>(`${this.apiUrl}/skills/report`, model);
  }

  public getCallTypesReport(model: CallTypesReportRequestApiModel): Observable<CallTypesReportApiModel> {
    return this.http.post<CallTypesReportApiModel>(`${this.apiUrl}/call-types/report`, model);
  }

  public getSkillsXlsx(model: SkillsReportRequestApiModel): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/skills/xlsx`, model, { responseType: 'blob' });
  }

  public getCallTypesXlsx(model: CallTypesReportRequestApiModel): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/call-types/xlsx`, model, { responseType: 'blob' });
  }

  public getSkillsPrint(model: PrintApiModel<SkillsReportRequestApiModel>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/skills/pdf`, model, { responseType: 'blob' });
  }

  public getCallTypesPrint(model: PrintApiModel<CallTypesReportRequestApiModel>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/call-types/pdf`, model, { responseType: 'blob' });
  }

  public getTraineePerformanceFilterData(): Observable<TraineePerformanceReportFiltersApiModel> {
    return this.http.get<TraineePerformanceReportFiltersApiModel>(`${this.apiUrl}/trainee-performance/filters`);
  }

  public getTraineePerformanceReport(
    filters: TraineePerformanceReportRequestApiModel
  ): Observable<TraineePerformanceReportApiModel> {
    return this.http.post<TraineePerformanceReportApiModel>(`${this.apiUrl}/trainee-performance/report`, filters);
  }

  public getTraineePerformancePrint(model: PrintApiModel<TraineePerformanceReportRequestApiModel>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/trainee-performance/pdf`, model, { responseType: 'blob' });
  }

  public getTraineePerformanceXlsx(filters: TraineePerformanceReportRequestApiModel): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/trainee-performance/xlsx`, filters, { responseType: 'blob' });
  }

  public getTrendingFilterData(): Observable<TrendsReportFiltersApiModel> {
    return this.http.get<TrendsReportFiltersApiModel>(`${this.apiUrl}/trends/filters`);
  }

  public getTrendingReport(filters: TrendsReportRequestApiModel): Observable<TrendsReportApiModel> {
    return this.http.post<TrendsReportApiModel>(`${this.apiUrl}/trends/report`, filters);
  }

  public getTrendingPrint(model: PrintApiModel<TrendsReportRequestApiModel>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/trends/pdf`, model, { responseType: 'blob' });
  }

  public getTrendingXlsx(filters: TrendsReportRequestApiModel): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/trends/xlsx`, filters, { responseType: 'blob' });
  }

  public getTrainerHoursFilterData(): Observable<TrainerHoursReportFiltersApiModel> {
    return this.http.get<TrainerHoursReportFiltersApiModel>(`${this.apiUrl}/trainer-hours/filters`);
  }

  public getTrainerHoursReport(filters: TrainerHoursReportRequestApiModel): Observable<TrainerHoursReportApiModel> {
    return this.http.post<TrainerHoursReportApiModel>(`${this.apiUrl}/trainer-hours/report`, filters);
  }

  public getTrainerHoursPrint(model: PrintApiModel<TrainerHoursReportRequestApiModel>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/trainer-hours/pdf`, model, { responseType: 'blob' });
  }

  public getTrainerHoursXlsx(filters: TrainerHoursReportRequestApiModel): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/trainer-hours/xlsx`, filters, { responseType: 'blob' });
  }

  public getCustomQuestionsFilterData(
    model: CustomQuestionsReportFiltersRequestApiModel
  ): Observable<CustomQuestionsReportFiltersApiModel> {
    return this.http.post<CustomQuestionsReportFiltersApiModel>(`${this.apiUrl}/custom-questions-filter-data`, model);
  }

  public getCustomQuestionsReport(
    filters: CustomQuestionsReportRequestApiModel
  ): Observable<CustomQuestionsReportApiModel> {
    return this.http.post<CustomQuestionsReportApiModel>(`${this.apiUrl}/custom-questions-report`, filters);
  }

  public getCustomQuestionsPrint(model: PrintApiModel<CustomQuestionsReportRequestApiModel>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/custom-questions-print`, model, { responseType: 'blob' });
  }

  public getCustomQuestionsXlsx(model: CustomQuestionsReportRequestApiModel): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/custom-questions-xlsx`, model, { responseType: 'blob' });
  }

  public getTrainerPerformanceFilterData(): Observable<TrainerPerformanceReportFiltersApiModel> {
    return this.http.get<TrainerPerformanceReportFiltersApiModel>(`${this.apiUrl}/trainer-performance/filters`);
  }

  public getTrainerPerformanceReport(
    filters: TrainerPerformanceReportRequestApiModel
  ): Observable<TrainerPerformanceReportApiModel> {
    return this.http.post<TrainerPerformanceReportApiModel>(`${this.apiUrl}/trainer-performance/report`, filters);
  }

  public getTrainerPerformancePrint(model: PrintApiModel<TrainerPerformanceReportRequestApiModel>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/trainer-performance/pdf`, model, { responseType: 'blob' });
  }

  public getTrainerPerformanceXlsx(filters: TrainerPerformanceReportRequestApiModel): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/trainer-performance/xlsx`, filters, { responseType: 'blob' });
  }
}
